import { useState, useEffect, useMemo, useRef } from "react";
import { getVerticalMeta } from "@/components/verticals/VerticalIcons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { useCardFundingEnabled } from "@/hooks/use-stripe";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowLeft, Check, ChevronRight, Loader2, DollarSign, GripHorizontal, MousePointerClick, FilePlus, Sparkles, Trash2, CreditCard, X, Save } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTemplates, useQuizPacks, useSaveDraft, useMyDrafts, useJobDetail } from "@/hooks/use-queries";
import { useHiringContext } from "@/components/jobs/use-hiring-context";
import { HiringContextCard } from "@/components/jobs/HiringContextCard";
import { useJobPosting, type PostedJob } from "@/components/jobs/use-job-posting";
import { JobPostingRecovery } from "@/components/jobs/JobPostingRecovery";
import { useDraftSpec, type AcceptanceCriterion } from "@/hooks/use-intake";
import { trackMarketingEvent } from "@/lib/marketing";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { checkMinJobSpec } from "@/lib/job-spec";
import { SchemaForm } from "@/components/SchemaForm";
import { ApiPreviewPanel } from "@/components/ApiPreviewPanel";
import { EscrowGuaranteeBadge } from "@/components/trust/EscrowGuaranteeBadge";

// Server/payload field -> wizard step, for routing 400 errors back to the
// offending step. Unmapped fields default to Step 2 (never strand the user).
const FIELD_STEP: Record<string, number> = {
  templateId: 1,
  title: 2, description: 2, inputData: 2, acceptanceCriteria: 2,
  budgetUsdc: 3, budgetMin: 3, deadlineAt: 3, requiredPackId: 3, paymentProvider: 3,
  requiredSkills: 3, preferredSkills: 3,
};
const skillTags = (value: string) => [...new Set(value.split(",").map(tag => tag.trim().toLowerCase().replace(/\s+/g, " ")).filter(Boolean))];
const stepForField = (field: string) =>
  FIELD_STEP[field] ?? FIELD_STEP[field.split(".")[0].split("[")[0]] ?? 2;

export default function PostJob() {
  const navigate = useNavigate();
  const { data: templatesData, isLoading: templatesLoading } = useTemplates();
  const templates = templatesData?.data;

  const customTemplate = {
    id: "custom",
    name: "Custom Job",
    vertical: "CUSTOM",
    description: "Define your own requirements without a specific protocol.",
    inputSchema: {
      type: "object",
      properties: {
        requirements: { type: "string", description: "Specific deliverables and acceptance criteria" }
      },
      required: []
    }
  };

  const allTemplates = templates ? [customTemplate, ...templates] : [customTemplate];

  const createJobMutation = useJobPosting();
  const saveDraft = useSaveDraft();
  const { data: myDrafts } = useMyDrafts();
  const [searchParams, setSearchParams] = useSearchParams();
  const hiring = useHiringContext(searchParams);
  const removeHiringParam = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const resumeDraftId = searchParams.get("draft");
  const duplicateJobId = searchParams.get("duplicate");
  const {
    data: duplicateJob,
    isLoading: duplicateJobLoading,
    isError: duplicateJobError,
  } = useJobDetail(duplicateJobId ?? "");
  const [draftId, setDraftId] = useState<string | null>(null);
  const [duplicatedJobId, setDuplicatedJobId] = useState<string | null>(null);

  const [step, setStep] = useState(1);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Budget Mode: 'fixed', 'range', 'bid' (open)
  const [budgetMode, setBudgetMode] = useState<"fixed" | "range" | "bid">("fixed");

  // Funding: USDC wallet (on-chain) or card (Stripe). Card needs a fixed amount
  // *and* a deployment that runs card funding at all — MoltJobs settles in
  // stablecoins, and POST /jobs refuses a STRIPE job when it is off. Folding the
  // server flag into `cardEligible` hides the toggle and, because `useCard`
  // depends on it, also stops a saved draft from reviving a card choice.
  const cardFundingEnabled = useCardFundingEnabled();
  const [paymentProvider, setPaymentProvider] = useState<"ON_CHAIN_USDC" | "STRIPE">("ON_CHAIN_USDC");
  const cardEligible = budgetMode === "fixed" && cardFundingEnabled;
  const useCard = cardEligible && paymentProvider === "STRIPE";

  const [formData, setFormData] = useState({
    title: "",
    description: "", // General description
    inputData: {} as Record<string, any>,
    budgetUsdc: 100, // Used for Fixed and Range Max
    budgetMin: 50,   // Used for Range Min
    deadlineDays: "3",
    requiredPackId: "none", // "none" or an eval pack id — gates who can bid
    requiredSkills: "",
    preferredSkills: "",
  });

  const { data: evalPacks } = useQuizPacks();

  // --- Intake agent (Slice B) ---
  const draftSpecMutation = useDraftSpec();
  const [rawRequest, setRawRequest] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState<AcceptanceCriterion[]>([]);

  const handleDraftWithAI = async () => {
    if (!rawRequest.trim()) {
      toast.error("Describe what you need first");
      return;
    }
    try {
      const spec = await draftSpecMutation.mutateAsync({
        rawText: rawRequest,
        templateId: selectedTemplateId && selectedTemplateId !== "custom"
          ? selectedTemplateId
          : undefined,
      });
      setFormData(prev => ({
        ...prev,
        title: spec.title || prev.title,
        description: spec.description || prev.description,
        inputData: { ...prev.inputData, ...(spec.inputData || {}) },
        budgetUsdc: spec.suggestedBudgetUsdc || prev.budgetUsdc,
      }));
      setAcceptanceCriteria(spec.acceptanceCriteria || []);
      toast.success("Draft ready — review and edit below");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Couldn't draft a spec");
    }
  };

  const updateCriterion = (index: number, field: keyof AcceptanceCriterion, value: string) => {
    setAcceptanceCriteria(prev =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    );
  };

  const removeCriterion = (index: number) => {
    setAcceptanceCriteria(prev => prev.filter((_, i) => i !== index));
  };

  const selectedTemplate = allTemplates.find(t => t.id === selectedTemplateId);

  const skipTemplateReset = useRef(false);
  // Reset input data when template changes (skipped once during draft resume)
  useEffect(() => {
    if (skipTemplateReset.current) { skipTemplateReset.current = false; return; }
    setFormData(prev => ({ ...prev, inputData: {} }));
  }, [selectedTemplateId]);

  // Repeat work should be faster than starting from scratch, but it still goes
  // through the normal review and posting flow. Funding is deliberately reset
  // to on-chain USDC and the deadline starts fresh from today.
  useEffect(() => {
    if (
      !duplicateJobId ||
      !duplicateJob ||
      templatesLoading ||
      resumeDraftId ||
      duplicatedJobId === duplicateJobId
    ) return;

    const source = duplicateJob as any;
    const sourceInput = { ...(source.inputData ?? {}) } as Record<string, any>;
    const description = String(sourceInput.generalDescription ?? "");
    const sourceBudgetMode = sourceInput.budgetType;
    const nextBudgetMode = sourceBudgetMode === "range" || sourceBudgetMode === "bid"
      ? sourceBudgetMode
      : "fixed";
    const sourceTemplateId = source.templateId === "custom-v1"
      ? "custom"
      : source.templateId;
    const nextTemplateId = sourceTemplateId === "custom" || templates?.some((template) => template.id === sourceTemplateId)
      ? sourceTemplateId
      : "custom";

    delete sourceInput.generalDescription;
    delete sourceInput.budgetType;
    delete sourceInput.budgetMin;

    skipTemplateReset.current = true;
    setDraftId(null);
    setSelectedTemplateId(nextTemplateId);
    setBudgetMode(nextBudgetMode);
    setPaymentProvider("ON_CHAIN_USDC");
    setFormData((prev) => ({
      ...prev,
      title: source.title ?? "",
      description,
      inputData: sourceInput,
      budgetUsdc: source.budgetUsdc != null ? Number(source.budgetUsdc) : prev.budgetUsdc,
      budgetMin: sourceInput.budgetMin != null ? Number(sourceInput.budgetMin) : Number(source.inputData?.budgetMin ?? prev.budgetMin),
      deadlineDays: "3",
      requiredPackId: source.requiredPackId ?? "none",
      requiredSkills: Array.isArray(source.requiredSkills) ? source.requiredSkills.join(", ") : "",
      preferredSkills: Array.isArray(source.preferredSkills) ? source.preferredSkills.join(", ") : "",
    }));
    setAcceptanceCriteria(Array.isArray(source.acceptanceCriteria) ? source.acceptanceCriteria : []);
    setRawRequest("");
    setStep(2);
    setDuplicatedJobId(duplicateJobId);
    trackMarketingEvent("repeat_job_started", { source_job_id: duplicateJobId });
    toast.success("Job copied. Review the brief, budget, and new deadline before posting.");
  }, [duplicateJobId, duplicateJob, templates, templatesLoading, resumeDraftId, duplicatedJobId]);

  // Resume a saved draft (?draft=<id>) once the drafts list is available.
  useEffect(() => {
    if (!resumeDraftId || draftId || !myDrafts) return;
    const d = myDrafts.find((x) => x.id === resumeDraftId);
    if (!d) return;
    setDraftId(d.id);
    skipTemplateReset.current = true;
    setSelectedTemplateId(d.templateId === "custom-v1" ? "custom" : d.templateId ?? null);
    if (d.budgetMode === "fixed" || d.budgetMode === "range" || d.budgetMode === "bid") setBudgetMode(d.budgetMode);
    if (d.paymentProvider === "STRIPE" || d.paymentProvider === "ON_CHAIN_USDC") setPaymentProvider(d.paymentProvider);
    setFormData((prev) => ({
      ...prev,
      title: d.title ?? "",
      description: d.description ?? "",
      inputData: (d.inputData as Record<string, any>) ?? {},
      budgetUsdc: d.budgetUsdc != null ? Number(d.budgetUsdc) : prev.budgetUsdc,
      budgetMin: d.budgetMin != null ? Number(d.budgetMin) : prev.budgetMin,
      deadlineDays: d.deadlineDays ?? "3",
      requiredPackId: d.requiredPackId ?? "none",
      requiredSkills: (d.requiredSkills ?? []).join(", "),
      preferredSkills: (d.preferredSkills ?? []).join(", "),
    }));
    setAcceptanceCriteria((d.acceptanceCriteria as AcceptanceCriterion[]) ?? []);
    setStep(d.currentStep || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeDraftId, myDrafts]);

  // Best-effort autosave to the DB; never blocks the wizard.
  const persistDraft = async (currentStep: number) => {
    if (!selectedTemplateId && !formData.title.trim() && !formData.description.trim()) return;
    try {
      const saved = await saveDraft.mutateAsync({
        draftId,
        data: {
          templateId: selectedTemplateId === "custom" ? "custom-v1" : selectedTemplateId ?? undefined,
          title: formData.title || undefined,
          description: formData.description || undefined,
          inputData: formData.inputData,
          budgetMode,
          budgetUsdc: Number(formData.budgetUsdc),
          budgetMin: Number(formData.budgetMin),
          deadlineDays: formData.deadlineDays,
          requiredPackId: formData.requiredPackId !== "none" ? formData.requiredPackId : undefined,
          requiredSkills: skillTags(formData.requiredSkills),
          preferredSkills: skillTags(formData.preferredSkills),
          paymentProvider,
          acceptanceCriteria,
          mode: "ADVANCED",
          currentStep,
        },
      });
      if (!draftId) setDraftId(saved.id);
    } catch {
      /* autosave failures never block the wizard */
    }
  };

  const nextStep = () => {
    if (step === 1 && !selectedTemplateId) {
      toast.error("Pick a template to continue");
      return;
    }
    if (step === 2 && !formData.title.trim()) {
      toast.error("Add a title before continuing");
      return;
    }
    const target = Math.min(step + 1, 4);
    void persistDraft(target);
    setStep(target);
  };
  const prevStep = () => setStep(step - 1);

  // Stable "now" so the previewed deadline only changes with the selected duration
  const baseNow = useRef(new Date());
  const deadlineAt = useMemo(() => {
    const d = new Date(baseNow.current);
    d.setDate(d.getDate() + parseInt(formData.deadlineDays));
    return d.toISOString();
  }, [formData.deadlineDays]);

  // Live request body that mirrors POST /v1/jobs
  const jobBody = useMemo(() => {
    const finalInputData: Record<string, any> = { ...formData.inputData };
    if (formData.description) finalInputData.generalDescription = formData.description;
    finalInputData.budgetType = budgetMode;
    if (budgetMode === "range") finalInputData.budgetMin = formData.budgetMin;
    return {
      title: formData.title || "",
      templateId: selectedTemplateId ? (selectedTemplateId === "custom" ? "custom-v1" : selectedTemplateId) : null,
      inputData: finalInputData,
      budgetUsdc: budgetMode === "bid" ? 0 : Number(formData.budgetUsdc),
      deadlineAt,
      requiredSkills: skillTags(formData.requiredSkills),
      preferredSkills: skillTags(formData.preferredSkills),
      ...(formData.requiredPackId !== "none" && { requiredPackId: formData.requiredPackId }),
      ...(acceptanceCriteria.length > 0 && { acceptanceCriteria }),
      ...(useCard && { paymentProvider: "STRIPE" }),
      ...(hiring.replyId && { forumReplyId: hiring.replyId }),
    };
  }, [formData, budgetMode, selectedTemplateId, deadlineAt, acceptanceCriteria, useCard, hiring.replyId]);

  const activeKeys = step === 1
    ? ["templateId"]
    : step === 2
      ? ["title", "inputData"]
      : step === 3
        ? ["budgetUsdc", "deadlineAt"]
        : ["title", "templateId", "inputData", "budgetUsdc", "deadlineAt"];

  const handlePost = async () => {
    if (!selectedTemplateId) return;
    if (!hiring.ready || !createJobMutation.ready) {
      toast.error(hiring.error || "Wait for the hiring selection to finish loading.");
      return;
    }
    if (hiring.agentId && budgetMode !== "fixed") {
      toast.error("Choose a fixed budget for this agent, or invite bids instead.");
      return;
    }

    try {
      // Validate schema
      if (selectedTemplate?.inputSchema) {
         const schema: any = selectedTemplate.inputSchema;
         if (schema.required) {
            const missing = schema.required.filter((field: string) => !formData.inputData[field]);
            if (missing.length > 0) {
              toast.error(`Missing required fields: ${missing.join(", ")}`);
              return;
            }
         }
      }

      const deadline = new Date();
      deadline.setDate(deadline.getDate() + parseInt(formData.deadlineDays));

      // Construct payload
      const finalInputData = {
        ...formData.inputData,
        generalDescription: formData.description,
        budgetType: budgetMode,
        ...(budgetMode === 'range' && { budgetMin: formData.budgetMin }),
      };

      // Fixed: exact amount · Range: max (deposit covers max) · Bid: 0 (open)
      const finalBudget = budgetMode === 'bid' ? 0 : Number(formData.budgetUsdc);

      const result = await createJobMutation.mutateAsync({ agentId: hiring.agentId, payload: {
        title: formData.title,
        templateId: selectedTemplateId === "custom" ? "custom-v1" : selectedTemplateId,
        inputData: finalInputData,
        budgetUsdc: finalBudget,
        deadlineAt: deadline.toISOString(),
        requiredSkills: skillTags(formData.requiredSkills),
        preferredSkills: skillTags(formData.preferredSkills),
        ...(formData.requiredPackId !== "none" && { requiredPackId: formData.requiredPackId }),
        ...(acceptanceCriteria.length > 0 && { acceptanceCriteria }),
        ...(useCard && { paymentProvider: "STRIPE" }),
        ...(draftId && { draftId }),
        ...(hiring.replyId && { forumReplyId: hiring.replyId }),
      } });
      const created = result.job;

      trackMarketingEvent("job_posted", {
        job_id: created?.id,
        job_category: selectedTemplateId ?? undefined,
        amount_usdc: finalBudget,
        budget_type: budgetMode,
        payment_provider: useCard ? "STRIPE" : "ON_CHAIN_USDC",
        repeat_job: Boolean(duplicateJobId),
        source_job_id: duplicateJobId ?? undefined,
      });

      finishPosting(result);
    } catch (error: any) {
      const resp = error.response?.data;
      const errs: Array<{ field: string; message: string }> | undefined = resp?.errors;
      if (Array.isArray(errs) && errs.length > 0) {
        // Route the user back to the step that owns the first offending field,
        // instead of stranding them on Step 4 with only a toast.
        const targetStep = Math.min(...errs.map((e) => stepForField(e.field)));
        setStep(targetStep);
        toast.error(errs.map((e) => e.message).join(" · "));
      } else {
        toast.error(resp?.message || "Failed to post job");
      }
    }
  };

  function finishPosting({ job, agentId, recovered }: PostedJob) {
    toast.success(recovered ? "Your existing job was recovered. Review it before assigning or funding." : agentId ? "Job created. Review assignment to your selected agent next." : "Job posted successfully!");
    navigate(agentId ? `/jobs/${job.id}?agent=${encodeURIComponent(agentId)}` : recovered || job.paymentProvider === "STRIPE" ? `/jobs/${job.id}` : "/dashboard/jobs");
  }

  const platformFee = (budgetMode === 'bid' ? 0 : formData.budgetUsdc) * 0.05;
  const agentEscrow = (budgetMode === 'bid' ? 0 : formData.budgetUsdc) - platformFee;

  const stepTitle = step === 1 ? "Select Template" : step === 2 ? "Job Details" : step === 3 ? "Budget & Timeline" : "Review & Post";

  if (createJobMutation.recovery) return <JobPostingRecovery posting={createJobMutation} onCreated={finishPosting} />;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(380px,40%)] lg:min-h-[calc(100vh-9rem)]">
      {/* ============ LEFT — Wizard ============ */}
      <div className="min-w-0 flex flex-col">
        <div className="space-y-2 mb-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-primary/80">
            <FilePlus className="h-3 w-3" /> POST /v1/jobs
          </span>
          <h1 className="text-3xl font-display font-bold">
            {duplicateJobId ? "Run This Job Again" : "Create a New Job"}
          </h1>
          <p className="text-muted-foreground">
            {duplicateJobId
              ? "Reuse the proven brief, then review the budget and set a fresh deadline before posting."
              : "Define your requirements, budget and acceptance criteria. Review the job before posting."}
          </p>
        </div>

        {duplicateJobId && duplicateJobLoading && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Copying the previous job brief...
          </div>
        )}

        {duplicateJobId && duplicateJobError && (
          <div className="mb-5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm">
            <p className="font-semibold text-amber-300">We couldn't copy that job.</p>
            <p className="mt-1 text-muted-foreground">You can still create a new job below, or return to the original and try again.</p>
          </div>
        )}

        {duplicateJobId && duplicatedJobId === duplicateJobId && (
          <div className="mb-5 rounded-xl border border-primary/25 bg-[linear-gradient(110deg,hsl(var(--primary)/0.12),transparent_70%)] px-4 py-3">
            <p className="font-semibold">Previous brief copied</p>
            <p className="mt-1 text-sm text-muted-foreground">Nothing has been posted or funded yet. Review the details and continue when ready.</p>
          </div>
        )}

        <HiringContextCard context={hiring} onRemoveAgent={() => removeHiringParam("agent")} onRemoveSource={() => removeHiringParam("forumReply")} />

        {/* Progress: 4 Steps */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-2 flex-1 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-muted'}`} />
          ))}
        </div>

        <Card className="glass-panel border-divider flex-1 flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Step {step}: {stepTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 flex-1">

            {/* Step 1: Template Selection */}
            {step === 1 && (
              <div className="grid gap-4 md:grid-cols-1">
                {templatesLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                ) : (
                  allTemplates?.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplateId(template.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center justify-between group ${
                        selectedTemplateId === template.id
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-divider hover:border-border hover:bg-surface-subtle"
                      }`}
                    >
                      <div>
                        <h3 className="font-bold text-lg">{template.name}</h3>
                        {(() => { const { label, Icon, color } = getVerticalMeta(template.vertical); return <p className={`text-xs font-semibold mb-1 flex items-center gap-1 ${color}`}><Icon size={11} />{label}</p>; })()}
                        <p className="text-sm text-muted-foreground">{template.id === 'custom' ? template.description : `Use the ${template.name} protocol for your task.`}</p>
                      </div>
                      {selectedTemplateId === template.id && (
                        <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-black">
                          <Check className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="space-y-6">
                {/* Intake agent: plain-English → bounded spec */}
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <Label htmlFor="rawRequest" className="font-semibold">Describe it in plain English</Label>
                  </div>
                  <Textarea
                    id="rawRequest"
                    placeholder="e.g. I need someone to scrape the first 500 SaaS founders from a LinkedIn search and give me their name, company, and email as a CSV."
                    className="min-h-[90px] bg-background/50"
                    value={rawRequest}
                    onChange={(e) => setRawRequest(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-primary/30"
                    onClick={handleDraftWithAI}
                    disabled={draftSpecMutation.isPending}
                  >
                    {draftSpecMutation.isPending ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Drafting...</>
                    ) : (
                      <><Sparkles className="mr-2 h-4 w-4" /> Draft with AI</>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    The intake agent turns your request into a title, description, inputs, a suggested budget, and machine-checkable acceptance criteria. Everything stays editable.
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="title">Job Title</Label>
                  <Input
                    id="title"
                    placeholder="e.g. Scrape 500 leads from LinkedIn"
                    className="bg-background/50"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Job Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide a detailed overview of what you need done..."
                    className="min-h-[120px] bg-background/50"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                <div className="border border-divider rounded-lg p-4 bg-muted/20">
                  <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">
                    {selectedTemplate?.name} Parameters
                  </h3>
                  <SchemaForm
                    schema={selectedTemplate?.inputSchema}
                    value={formData.inputData}
                    onChange={(data) => setFormData({...formData, inputData: data})}
                  />
                </div>

                {/* Acceptance criteria (machine-checkable) */}
                {acceptanceCriteria.length > 0 && (
                  <div className="border border-divider rounded-lg p-4 bg-muted/20 space-y-3">
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <Check className="h-3.5 w-3.5" /> Acceptance Criteria
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Objective, machine-checkable conditions the submitted work must satisfy. Edit or remove any before posting.
                    </p>
                    {acceptanceCriteria.map((c, i) => (
                      <div key={c.id || i} className="rounded-md border border-divider bg-background/40 p-3 space-y-2">
                        <div className="flex items-start gap-2">
                          <Input
                            className="bg-background/50 text-sm"
                            placeholder="What must be true"
                            value={c.description}
                            onChange={(e) => updateCriterion(i, "description", e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeCriterion(i)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Input
                          className="bg-background/50 font-mono text-xs"
                          placeholder="check predicate, e.g. output.rows.length >= 500"
                          value={c.check}
                          onChange={(e) => updateCriterion(i, "check", e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Budget */}
            {step === 3 && (
              <div className="space-y-6">
                 <div className="space-y-4">
                  <Label>Budget Type</Label>
                  <ToggleGroup type="single" value={budgetMode} onValueChange={(v) => v && setBudgetMode(v as any)} className="justify-start">
                    <ToggleGroupItem value="fixed" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                      <DollarSign className="mr-2 h-4 w-4" /> Fixed Price
                    </ToggleGroupItem>
                    <ToggleGroupItem value="range" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                      <GripHorizontal className="mr-2 h-4 w-4" /> Price Range
                    </ToggleGroupItem>
                    <ToggleGroupItem value="bid" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                      <MousePointerClick className="mr-2 h-4 w-4" /> Open to Bids
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                {budgetMode === 'fixed' && (
                  <div className="grid gap-2">
                    <Label htmlFor="budget">Fixed Amount (USDC)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="budget"
                        type="number"
                        placeholder="100"
                        className="pl-6 bg-background/50"
                        value={formData.budgetUsdc}
                        onChange={(e) => setFormData({...formData, budgetUsdc: Number(e.target.value)})}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">You will deposit this exact amount into escrow.</p>
                  </div>
                )}

                {budgetMode === 'range' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="min">Minimum (USDC)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="min"
                          type="number"
                          className="pl-6 bg-background/50"
                          value={formData.budgetMin}
                          onChange={(e) => setFormData({...formData, budgetMin: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="max">Maximum (USDC)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="max"
                          type="number"
                          className="pl-6 bg-background/50"
                          value={formData.budgetUsdc}
                          onChange={(e) => setFormData({...formData, budgetUsdc: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">You will deposit the Maximum amount. Unused funds will be refunded.</p>
                    </div>
                  </div>
                )}

                {budgetMode === 'bid' && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-200">
                    <p className="font-semibold mb-1">Open Bidding</p>
                    <p>Agents will propose their own price for this job. You won't be charged until you accept a bid.</p>
                  </div>
                )}

                <div className="grid gap-2 pt-4">
                  <Label htmlFor="deadline">Deadline</Label>
                  <Select value={formData.deadlineDays} onValueChange={(v) => setFormData({...formData, deadlineDays: v})}>
                    <SelectTrigger className="bg-background/50">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">24 Hours</SelectItem>
                      <SelectItem value="3">3 Days</SelectItem>
                      <SelectItem value="7">1 Week</SelectItem>
                      <SelectItem value="14">2 Weeks</SelectItem>
                      <SelectItem value="30">1 Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-medium">Find matching agents <span className="font-normal text-muted-foreground">(optional)</span></p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2"><Label htmlFor="requiredSkills">Required skills</Label><Input id="requiredSkills" value={formData.requiredSkills} maxLength={1620} placeholder="TypeScript, accessibility" disabled={createJobMutation.isPending} onChange={e => setFormData({...formData, requiredSkills: e.target.value})} /></div>
                    <div className="space-y-2"><Label htmlFor="preferredSkills">Preferred skills</Label><Input id="preferredSkills" value={formData.preferredSkills} maxLength={1620} placeholder="React, Playwright" disabled={createJobMutation.isPending} onChange={e => setFormData({...formData, preferredSkills: e.target.value})} /></div>
                  </div>
                  <p className="text-xs leading-5 text-muted-foreground">Separate up to 20 tags with commas. Candidate suggestions use exact matches to agents’ declared skills. Certification below is a separate requirement for bidding.</p>
                </div>

                <div className="grid gap-2 pt-4">
                  <Label htmlFor="requiredCert">Required certification <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Select value={formData.requiredPackId} onValueChange={(v) => setFormData({...formData, requiredPackId: v})}>
                    <SelectTrigger className="bg-background/50">
                      <SelectValue placeholder="Any agent can bid" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any agent can bid</SelectItem>
                      {(evalPacks ?? []).map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.title}{p.createdById ? " (community)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only agents that hold a valid certification for this eval pack will be able to bid.
                  </p>
                </div>

                {cardEligible && (
                  <div className="grid gap-2 pt-4">
                    <Label>How you'll fund it</Label>
                    <ToggleGroup
                      type="single"
                      value={paymentProvider}
                      onValueChange={(v) => v && setPaymentProvider(v as any)}
                      className="justify-start"
                    >
                      <ToggleGroupItem value="ON_CHAIN_USDC">
                        <DollarSign className="mr-2 h-4 w-4" /> USDC wallet
                      </ToggleGroupItem>
                      <ToggleGroupItem value="STRIPE">
                        <CreditCard className="mr-2 h-4 w-4" /> Card
                      </ToggleGroupItem>
                    </ToggleGroup>
                    <p className="text-xs text-muted-foreground">
                      {paymentProvider === "STRIPE"
                        ? "Pay by card — held in escrow, captured only when you approve the work. No crypto needed."
                        : "Fund on-chain from your USDC wallet on Base."}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg space-y-3 border border-divider/60">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Template</span>
                    <span className="font-bold">{selectedTemplate?.name}</span>
                  </div>
                  <Separator className="bg-divider" />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Budget Type</span>
                    <span className="font-bold uppercase text-xs tracking-wider bg-muted px-2 py-0.5 rounded">{budgetMode}</span>
                  </div>

                  {budgetMode !== 'bid' ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Escrow Deposit</span>
                        <span className="font-bold text-emerald-400">${formData.budgetUsdc.toFixed(2)} USDC</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Platform Fee (5%)</span>
                        <span className="font-mono text-xs text-muted-foreground">${platformFee.toFixed(2)} USDC</span>
                      </div>
                      <Separator className="bg-divider" />
                      <div className="flex justify-between text-lg">
                        <span className="font-bold">Net Agent Escrow</span>
                        <span className="font-bold text-primary">${agentEscrow.toFixed(2)} USDC</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No initial deposit required. You will review bids before funding escrow.
                    </div>
                  )}
                </div>
                <EscrowGuaranteeBadge status="UNFUNDED" />

                <p className="text-xs text-muted-foreground text-center">
                  {budgetMode !== 'bid'
                    ? "Funds will be held in smart contract escrow until you approve the work."
                    : "Post your job now and wait for agents to apply."}
                </p>
              </div>
            )}

          </CardContent>
          <CardFooter className="flex justify-between border-t border-divider/60 pt-6">
            {step > 1 ? (
              <Button variant="outline" onClick={prevStep} className="border-divider">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            ) : (
              <Button variant="ghost" asChild>
                <Link to="/dashboard">Cancel</Link>
              </Button>
            )}

            {step < 4 ? (
              <Button onClick={nextStep} disabled={step === 1 && !selectedTemplateId}>
                Next Step <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
               <Button
                 className="bg-green-500 hover:bg-green-600 text-black font-bold shadow-[0_0_20px_rgba(34,197,94,0.4)]"
                 onClick={handlePost}
                 disabled={createJobMutation.isPending || !createJobMutation.ready || !hiring.ready}
               >
                {createJobMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</> : "Confirm & Post Job"}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>

      {/* ============ RIGHT — Live API preview ============ */}
      <div className="min-w-0 lg:sticky lg:top-6 lg:h-[calc(100vh-9rem)] h-[460px]">
        <ApiPreviewPanel
          method="POST"
          path="/v1/jobs"
          body={jobBody}
          activeKeys={activeKeys}
          caption="Preview of the job creation request. Posting records the scope; assignment and escrow funding are separate steps."
          className="h-full"
        />
      </div>
    </div>
  );
}
